import { useState } from "react";

const variants = {
  primary: {
    background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
    color: "#fff",
    boxShadow: "0 6px 24px rgba(196, 135, 154, 0.42)",
  },
  gold: {
    background: "linear-gradient(135deg, var(--gold), #9A6A10)",
    color: "var(--noir)",
    boxShadow: "0 6px 24px rgba(201, 168, 76, 0.42)",
  },
  outline: {
    background: "transparent",
    color: "var(--accent)",
    border: "1.5px solid var(--border-strong)",
  },
  ghost: {
    background: "rgba(255, 255, 255, 0.08)",
    color: "rgba(255, 255, 255, 0.85)",
    border: "1.5px solid rgba(255, 255, 255, 0.18)",
  },
  neutral: {
    background: "var(--bg-elevated)",
    color: "var(--text)",
    border: "1.5px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  provider: {
    background: "var(--bg-elevated)",
    color: "var(--text)",
    border: "1.5px solid var(--border-strong)",
    boxShadow: "0 8px 28px rgba(13, 8, 16, 0.10)",
  },
  dark: {
    background: "var(--plum)",
    color: "var(--champagne)",
    border: "1px solid rgba(201, 168, 76, 0.3)",
  },
  danger: {
    background: "rgba(180, 50, 50, 0.08)",
    color: "#C02020",
    border: "1px solid rgba(180, 50, 50, 0.22)",
  },
};

const sizes = {
  sm: { padding: "8px 16px", fontSize: 12 },
  md: { padding: "12px 24px", fontSize: 13 },
  lg: { padding: "14px 32px", fontSize: 15 },
};

export function Button({
  label,
  icon,
  onClick,
  variant = "primary",
  size = "md",
  disabled,
  full,
  type = "button",
  ariaLabel,
  loading,
  className,
  style,
  ...props
}) {
  const [pressed, setPressed] = useState(false);
  const v = variants[variant] || variants.primary;
  const s = sizes[size] || sizes.md;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel || label}
      aria-busy={loading || undefined}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      className={className}
      {...props}
      style={{
        ...v,
        ...s,
        border: v.border || "none",
        borderRadius: 14,
        fontFamily: "var(--font-system)",
        fontWeight: 600,
        letterSpacing: 0.3,
        opacity: disabled || loading ? 0.45 : 1,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        transition:
          "transform .18s var(--ease-spring), box-shadow .25s var(--ease-out), opacity .2s",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        width: full ? "100%" : undefined,
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 44,
        justifyContent: full ? "center" : undefined,
        whiteSpace: "normal",
        textAlign: "center",
        lineHeight: 1.2,
        overflowWrap: "break-word",
        wordBreak: "normal",
        userSelect: "none",
        transform: pressed ? "scale(0.96)" : "scale(1)",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      {loading && (
        <span
          aria-hidden="true"
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "2px solid currentColor",
            borderTopColor: "transparent",
            animation: "spin .9s linear infinite",
          }}
        />
      )}
      {!loading && icon && <span aria-hidden="true" style={{ flexShrink: 0 }}>{icon}</span>}
      <span>{label}</span>
    </button>
  );
}
